// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import type { Offer, PurchaseState, ShoppingListItem } from "@bitebalance/contracts"
import { useI18n, formatEurFromCents, formatShortDate, formatFullDate } from "@/i18n"
import { unitLabel } from "@/lib/units"

type TFn = ReturnType<typeof useI18n>["t"]
import { EstimatedBadge, RealPriceBadge } from "@/components/state"
import { Checkbox } from "@/components/onboarding/Checkbox"
import { localNum, pantryCovered as isPantryCovered, rowNeed, rowSubtitle, shopRowName } from "./buyLine"
import { purchaseChoices, purchaseStatusText } from "./purchase"

export interface ShoppingRowProps {
  item: ShoppingListItem
  /** ADR-028: currently-valid offers whose matchedIngredientId equals this row's ingredient. */
  offers?: Offer[]
  onToggleChecked: (id: string, checked: boolean) => void
  /**
   * WS3 Shop → Pantry v1: the user says what came home — bought / already
   * had / not bought (an optional typed quantity in the row's canonical
   * unit corrects the server's default). Absent on pages that only list.
   */
  onConfirmPurchase?: (id: string, state: PurchaseState, quantity?: number) => void
}

function quantityText(q: ShoppingListItem["requiredQuantity"], t: TFn): string {
  if (!q) return "—"
  // canonical units never reach the user raw — "count" is an internal word
  // the number prints in the reader's decimal mark ("10,25 Stk."), digits untouched
  return `${localNum(q.display.quantity, t)} ${unitLabel(q.display.unit, t)}`
}

/**
 * One shopping-list row (T13): required/reserved/net/pack/buy/leftover +
 * expandable source meals. Manual rows legitimately carry `null` for every
 * calculated field (ShoppingListItemSchema's own comment) — rendered as
 * "—", never as a fabricated 0.
 */
export function ShoppingRow({ item, offers = [], onToggleChecked, onConfirmPurchase }: ShoppingRowProps) {
  const { t, locale } = useI18n()
  const [expanded, setExpanded] = useState(false)
  const [purchasedQtyText, setPurchasedQtyText] = useState("")
  const purchaseStatus = purchaseStatusText(item, t)
  const canConfirm = onConfirmPurchase !== undefined && !item.manual
  const purchaseUnit = unitLabel(item.purchase?.quantity?.canonical.unit ?? item.selectedPack?.unit ?? "g", t)

  const pantryCovered = isPantryCovered(item)
  // Shop list clarity (2026-10-06): the pack bought in words, and the need it was rounded up from beside it.
  const subtitle = rowSubtitle(item, t)
  const need = rowNeed(item, t)
  // the English list capitalises a calculated row's name (first character only); German and manual rows are served as typed
  const name = shopRowName(item, locale)

  return (
    <li className="rounded-bb-lg border border-line bg-paper">
      {/* Debt 38 (2026-09-07): this used to be one `role="button"` div wrapping
          the checkbox and the purchase buttons, with an Enter/Space handler
          that hijacked keys pressed on those nested controls. Now the
          checkbox is a sibling, the disclosure is a real <button> that owns
          only the expand affordance, and the browser handles its keys. */}
      <div className="flex w-full items-start justify-between gap-2 p-3">
        <span className="flex items-start gap-2">
          <Checkbox
            id={`shop-item-${item.id}`}
            checked={item.checked}
            onCheckedChange={checked => onToggleChecked(item.id, checked)}
          />
          <span>
            <button
              type="button"
              aria-expanded={expanded}
              aria-label={`${name} — ${t("views.shop.detailsA11y")}`}
              onClick={() => setExpanded(prev => !prev)}
              className={`cursor-pointer text-left font-medium text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-green ${
                item.checked ? "line-through opacity-60" : ""
              }`}
            >
              {name}
            </button>
            {subtitle && (
              <span
                className={`block text-xs ${pantryCovered ? "font-medium text-green" : "text-ink-2"}`}
                data-testid="shop-row-subtitle"
              >
                <span className={pantryCovered ? "" : "font-medium text-ink"}>{subtitle}</span>
                {need && <span data-testid="shop-row-need"> · {need}</span>}
              </span>
            )}
            {item.manual && (
              <span className="mt-0.5 inline-block rounded-bb-full bg-sky/30 px-2 py-0.5 text-xs font-medium text-ink-2">
                {t("views.shop.manualBadge")}
              </span>
            )}
            {purchaseStatus && (
              <span className="block text-xs font-medium text-green" data-testid="shop-row-purchase-status">
                {purchaseStatus}
              </span>
            )}
            {canConfirm && (
              <span
                role="group"
                aria-label={t("views.shop.purchaseStateA11y", { name })}
                className="mt-1 flex flex-wrap gap-1"
              >
                {purchaseChoices(item, t).map(choice => (
                  <button
                    key={choice.state}
                    type="button"
                    aria-pressed={choice.selected}
                    data-testid={`shop-purchase-${choice.state}`}
                    onClick={() => onConfirmPurchase?.(item.id, choice.state)}
                    className={`min-h-8 rounded-bb-full border px-2 py-0.5 text-xs font-medium ${
                      choice.selected
                        ? "border-green bg-green text-paper"
                        : "border-line bg-paper text-ink-2 hover:text-ink"
                    }`}
                  >
                    {choice.label}
                  </button>
                ))}
              </span>
            )}
          </span>
        </span>
        {item.priceEstimate && item.priceEstimate.status !== "unavailable" ? (
          <span className="flex flex-col items-end gap-0.5">
            <span
              className={`text-sm ${pantryCovered ? "text-ink-2" : "text-ink"}`}
              data-testid="shop-row-price"
            >
              {formatEurFromCents(item.priceEstimate.amountCents, locale)}
            </span>
            {!pantryCovered &&
              (item.priceEstimate.status === "cached" ? <RealPriceBadge /> : <EstimatedBadge />)}
          </span>
        ) : item.priceEstimate ? (
          // Status 'unavailable' (C-043): an honestly missing price — a
          // localized label, never a fake €0.00 figure or a bare glyph.
          <span className="text-xs text-ink-2">{t("views.shop.priceUnavailable")}</span>
        ) : (
          // Null (manual row): no price concept at all.
          <span className="text-sm text-ink-2">—</span>
        )}
      </div>

      {/* Offers are money on the table — visible even collapsed. */}
      {offers.length > 0 && (
        <div className="flex flex-col gap-1 px-3 pb-2">
          {offers.map(offer => (
            <OfferChip key={offer.id} offer={offer} />
          ))}
        </div>
      )}

      {expanded && (
        <div className="flex flex-col gap-2 border-t border-line p-3 pt-2">
          <dl className="grid grid-cols-2 gap-2 text-xs text-ink-2 sm:grid-cols-4">
            <div>
              <dt className="uppercase">{t("views.shop.requiredLabel")}</dt>
              <dd className="text-ink">{quantityText(item.requiredQuantity, t)}</dd>
            </div>
            <div>
              <dt className="uppercase">{t("views.shop.reservedLabel")}</dt>
              <dd className="text-ink">{quantityText(item.pantryReserved, t)}</dd>
            </div>
            <div>
              <dt className="uppercase">{t("views.shop.packLabel")}</dt>
              <dd className="text-ink">
                {item.selectedPack ? `${localNum(item.selectedPack.quantity, t)} ${unitLabel(item.selectedPack.unit, t)}` : "—"}
              </dd>
            </div>
            <div>
              <dt className="uppercase">{t("views.shop.leftoverLabel")}</dt>
              <dd className="text-ink">{quantityText(item.expectedLeftover, t)}</dd>
            </div>
          </dl>
          {/* Track A: a 'cached' price is real but never a per-store
              guarantee — provenance renders with the math, one tap away. */}
          {item.priceEstimate?.status === "cached" && item.priceEstimate.basis && (
            <p className="text-xs text-ink-2">{item.priceEstimate.basis}</p>
          )}
          {item.sourceMealIds.length > 0 && (
            <p className="text-xs font-medium text-green">
              {t("views.shop.sourceMealsLabel", { count: item.sourceMealIds.length })}
            </p>
          )}
          {canConfirm && item.purchase && item.purchase.state !== "not_bought" && (
            // Correct the confirmed quantity (canonical unit) — the server applies the difference to the Pantry.
            <form
              className="flex flex-wrap items-center gap-2 text-xs"
              onSubmit={e => {
                e.preventDefault()
                const parsed = Number(purchasedQtyText.replace(",", "."))
                if (!Number.isFinite(parsed) || parsed < 0) return
                onConfirmPurchase?.(item.id, item.purchase!.state, parsed)
                setPurchasedQtyText("")
              }}
            >
              <label className="flex items-center gap-1 text-ink-2">
                <span>{t("views.shop.purchaseQtyLabel", { unit: purchaseUnit })}</span>
                <input
                  type="number"
                  min={0}
                  step="any"
                  inputMode="decimal"
                  value={purchasedQtyText}
                  onChange={e => setPurchasedQtyText(e.target.value)}
                  className="w-24 rounded-bb-md border border-line bg-paper px-2 py-1 text-ink"
                  data-testid="shop-purchase-quantity"
                />
              </label>
              <button
                type="submit"
                className="min-h-8 rounded-bb-full border border-line px-2 py-0.5 font-medium text-ink-2 hover:text-ink"
              >
                {t("views.shop.purchaseQtySave")}
              </button>
            </form>
          )}
        </div>
      )}
    </li>
  )
}

/**
 * ADR-028 Phase 2 offer chip: compact retailer + captured package price +
 * validity on the row; the expanded state carries the full provenance
 * (raw offer title, source + capture date, the wire-provided basis string,
 * unit comparison price when the source supplied one) and the mandatory
 * "may vary by store" caveat. The offer price is the PACKAGE price exactly
 * as captured — never converted to a per-serving figure (spec rule 4).
 */
function OfferChip({ offer }: { offer: Offer }) {
  const { t, locale } = useI18n()
  const price = formatEurFromCents(offer.price.amountCents, locale)
  const validTo = formatShortDate(offer.validTo, locale)

  return (
    <details className="text-xs">
      <summary
        className="inline-flex cursor-pointer list-none flex-wrap items-center gap-1 rounded-bb-full border border-green/40 bg-green/10 px-2 py-0.5 font-medium text-ink-2 [&::-webkit-details-marker]:hidden"
        aria-label={t("views.shop.offerChipAria", {
          retailer: offer.retailer,
          price,
          date: validTo,
        })}
      >
        <span className="font-semibold text-green">{t("views.shop.offerBadge")}</span>
        <span className="font-semibold text-ink">{offer.retailer}</span>
        <span className="text-ink">{price}</span>
        <span>{t("views.shop.offerChipValidUntil", { date: validTo })}</span>
        <span>· {t("views.shop.offerMayVary")}</span>
      </summary>
      <div className="mt-1 flex flex-col gap-0.5 rounded-bb-lg border border-line bg-cream p-2 text-ink-2">
        {/* Raw retailer offer title, verbatim from the wire — never rewritten. */}
        <p className="font-medium text-ink">{offer.title}</p>
        <p>{t("views.shop.offerPackagePriceLabel", { price })}</p>
        {offer.unitInfo && offer.unitInfo.pricePerUnitCents !== null && (
          <p>
            {t("views.shop.offerUnitPrice", {
              price: formatEurFromCents(offer.unitInfo.pricePerUnitCents, locale),
              unit: offer.unitInfo.pricePerUnitBasis ?? offer.unitInfo.unit,
            })}
          </p>
        )}
        <p>
          {t("views.shop.offerSourceLine", {
            source: offer.source,
            date: formatFullDate(offer.capturedAt, locale),
          })}
        </p>
        {offer.price.basis && <p>{offer.price.basis}</p>}
        <p>{t("views.shop.offerMayVary")}</p>
      </div>
    </details>
  )
}
